"use client";

import * as React from "react";
import { Clock, X } from "lucide-react";

import { cn } from "@/utils/misc";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";

interface TimePickerProps {
  /** 24h time string (HH:MM), e.g. "09:00". */
  value?: string;
  /** Called with a 24h HH:MM string, or "" when cleared. */
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  /** Accessible name of the clear control. Defaults to "Clear time". */
  clearLabel?: string;
  /** Accessible name of the text field, when the placeholder is not enough. */
  inputAriaLabel?: string;
  /**
   * Narrow table-cell sizing: no clock button, shorter text, and only a
   * clear control when a time is set. The value is still typed ("9am", "14:30").
   */
  compact?: boolean;
  className?: string;
  /** Overrides the default input className. Use to match toolbar sizing. */
  inputClassName?: string;
}

/** "14:30" → "2:30 pm" for display. */
function formatDisplay(value: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return value;
  const hRaw = Number(m[1]);
  const suffix = hRaw >= 12 ? "pm" : "am";
  const h = hRaw % 12 || 12;
  return `${h}:${m[2]} ${suffix}`;
}

/**
 * Parse flexible user input into HH:MM (24h). Accepts "9", "9am", "9:30",
 * "9.30pm", "09:30", "1430", "2:30 PM".
 */
function parseUserInput(raw: string): string | null {
  const trimmed = raw.trim().toLowerCase().replace(/\s+/g, "");
  if (!trimmed) return null;

  const m = /^(\d{1,2})(?:[:.]?(\d{2}))?(am|pm)?$/.exec(trimmed);
  if (!m) return null;

  // "1430" style — 3-4 digit compact 24h time lands entirely in m[1]+m[2].
  const isCompact =
    m[1].length === 2 && m[2] === undefined && trimmed.length === 4 && !m[3];

  const baseHours = isCompact ? Number(trimmed.slice(0, 2)) : Number(m[1]);
  const minutes = isCompact
    ? Number(trimmed.slice(2))
    : m[2] !== undefined
      ? Number(m[2])
      : 0;

  const hours =
    m[3] === "pm" && baseHours < 12
      ? baseHours + 12
      : m[3] === "am" && baseHours === 12
        ? 0
        : baseHours;

  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** All half-hour options for the dropdown, as 24h HH:MM values. */
const TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const mm = i % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${mm}`;
});

/**
 * Time picker matching the app `DatePicker`: free-text input (accepts
 * "9am", "9:30", "14:30"…) plus a popover list of half-hour options.
 * Accepts and emits 24h HH:MM strings.
 */
export function TimePicker({
  value,
  onChange,
  placeholder = "Pick a time",
  disabled,
  clearable = false,
  clearLabel = "Clear time",
  inputAriaLabel,
  compact = false,
  className,
  inputClassName,
}: TimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement | null>(null);

  // Sync input text from value prop when not focused.
  React.useEffect(() => {
    if (!isFocused) {
      setInputText(value ? formatDisplay(value) : "");
    }
  }, [value, isFocused]);

  // Scroll the selected (or a sensible default) option into view on open.
  React.useEffect(() => {
    if (!open) return;
    const target = value ?? "09:00";
    const index = TIME_OPTIONS.findIndex((t) => t >= target);
    requestAnimationFrame(() => {
      const el = listRef.current?.children[Math.max(index, 0)] as
        HTMLElement | undefined;
      el?.scrollIntoView({ block: "center" });
    });
  }, [open, value]);

  const handleInputBlur = () => {
    setIsFocused(false);
    if (!inputText.trim()) {
      if (value && onChange) onChange("");
      return;
    }
    const parsed = parseUserInput(inputText);
    if (parsed && onChange) {
      onChange(parsed);
    } else {
      setInputText(value ? formatDisplay(value) : "");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <div className={cn("relative flex items-center", className)}>
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={handleInputBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={inputAriaLabel}
          className={cn(
            "w-full rounded-md border border-input bg-background tabular-nums placeholder-muted-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            compact ? "h-7 px-1.5 py-0 text-xs" : "h-10 py-2 pl-8 pr-7 text-sm",
            compact && clearable && value && "pr-5",
            inputClassName,
          )}
        />
        {!compact && (
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label="Pick a time"
              className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
            >
              <Clock className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
        )}
        {clearable && value && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange?.("");
              setInputText("");
            }}
            aria-label={clearLabel}
            className={cn(
              "absolute top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground",
              compact ? "right-1" : "right-2",
            )}
          >
            <X className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} />
          </button>
        )}
      </div>
      <PopoverContent
        className="w-36 p-1"
        align="start"
        side="bottom"
        avoidCollisions
      >
        <div ref={listRef} className="max-h-64 overflow-y-auto">
          {TIME_OPTIONS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                onChange?.(t);
                setOpen(false);
              }}
              className={cn(
                "w-full rounded-sm px-2 py-1.5 text-left text-sm tabular-nums transition-colors hover:bg-muted",
                t === value
                  ? "bg-primary/10 font-medium text-primary"
                  : "text-foreground",
              )}
            >
              {formatDisplay(t)}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
